// ─────────────────────────────────────────────────────────────
// Reward rules & anti-abuse gates. Dark theme, renders in admin shell.
// Uses your existing config endpoints:
//   GET   /mtrade/admin/config?group=rewards   (and ?group=features)
//   PATCH /mtrade/admin/config/:key            (body { value })
// ─────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { apiClient } from "../../../api/apiServices";

const MILESTONES = [
  { id: "first_fiat_deposit",   label: "First Naira Deposit",  amountKey: "reward.milestone.first_fiat_deposit",   hasMin: true  },
  { id: "first_crypto_deposit", label: "First Crypto Deposit", amountKey: "reward.milestone.first_crypto_deposit", hasMin: true  },
  { id: "first_buy",            label: "First Buy",            amountKey: "reward.milestone.first_buy",            hasMin: true  },
  { id: "first_sell",           label: "First Sell",           amountKey: "reward.milestone.first_sell",           hasMin: true  },
  { id: "first_bank_link",      label: "First Bank Link",      amountKey: "reward.milestone.first_bank_link",      hasMin: false },
  { id: "first_bill_payment",   label: "First Bill Payment",   amountKey: "reward.milestone.first_bill_payment",   hasMin: true  },
  { id: "first_kyc",            label: "KYC Completion",       amountKey: "reward.milestone.first_kyc",            hasMin: false, noKyc: true },
];

export default function AdminRewardRulesView() {
  const [vals, setVals]   = useState({});
  const [loading, setLoad] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    Promise.all([
      apiClient.get("/mtrade/admin/config?group=rewards"),
      apiClient.get("/mtrade/admin/config?group=features"),
    ]).then(([rw, ft]) => {
      const flat = {};
      const collect = (g) => Object.values(g || {}).flat().forEach(i => { if (i?.key) flat[i.key] = i.value; });
      if (rw.data.status) collect(rw.data.data);
      if (ft.data.status) collect(ft.data.data);
      setVals(flat);
    }).catch(() => {}).finally(() => setLoad(false));
  }, []);

  const save = async (key, value) => {
    setSavingKey(key);
    try {
      await apiClient.patch(`/mtrade/admin/config/${encodeURIComponent(key)}`, { value });
      setVals(v => ({ ...v, [key]: value }));
    } catch {} finally { setSavingKey(null); }
  };

  const Toggle = ({ k, fallback = true }) => (
    <button onClick={() => save(k, !(vals[k] ?? fallback))}
      className={`w-11 h-6 rounded-full relative transition-colors flex-shrink-0 ${(vals[k] ?? fallback) ? "bg-[#00C896]" : "bg-white/10"}`}>
      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${(vals[k] ?? fallback) ? "left-6" : "left-1"}`} />
    </button>
  );

  const NumInput = ({ k, prefix = "₦" }) => (
    <div className="flex items-center bg-[#1a1a1a] border border-white/8 rounded-lg px-2 focus-within:border-[#00C896]/50">
      {prefix && <span className="text-white/30 text-xs mr-1">{prefix}</span>}
      <input type="number" defaultValue={vals[k] ?? 0} onBlur={e => save(k, Number(e.target.value))}
        className="w-20 bg-transparent py-1.5 text-sm text-white outline-none" />
    </div>
  );

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-10 h-10 border-4 border-[#00C896] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const rewardsOn = vals["features.rewards_enabled"] ?? true;

  return (
    <div className="text-white max-w-4xl">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-white">Reward Rules &amp; Anti-Abuse</h1>
        <p className="text-white/40 text-sm mt-0.5">Per-reward switches, KYC gates and minimum amounts</p>
      </div>

      {/* Master switches */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="bg-[#161616] border border-white/5 rounded-xl p-4 flex items-center justify-between">
          <div><p className="text-white font-semibold text-sm">Rewards System</p><p className="text-white/40 text-xs">Master switch</p></div>
          <Toggle k="features.rewards_enabled" />
        </div>
        <div className="bg-[#161616] border border-white/5 rounded-xl p-4 flex items-center justify-between">
          <div><p className="text-white font-semibold text-sm">Require KYC (default)</p><p className="text-white/40 text-xs">Applies unless overridden below</p></div>
          <Toggle k="reward.require_kyc_default" />
        </div>
        <div className="bg-[#161616] border border-white/5 rounded-xl p-4 flex items-center justify-between">
          <div><p className="text-white font-semibold text-sm">Referral needs KYC</p><p className="text-white/40 text-xs">Referee must verify first</p></div>
          <Toggle k="reward.referral.require_kyc" />
        </div>
      </div>

      {!rewardsOn && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-400/10 border border-amber-400/20 rounded-xl mb-4">
          <i className="ri-information-line text-amber-400" />
          <p className="text-amber-400 text-xs">Rewards master switch is OFF — no rewards are being paid, regardless of the settings below.</p>
        </div>
      )}

      {/* Per-milestone table */}
      <div className="bg-[#161616] border border-white/5 rounded-xl overflow-hidden">
        <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 px-4 py-3 border-b border-white/5 text-[10px] uppercase tracking-wide text-white/30 font-bold">
          <span>Reward</span><span className="text-center">On</span><span className="text-center">KYC</span><span className="text-right">Min Amount</span>
        </div>
        <div className="divide-y divide-white/4">
          {MILESTONES.map(m => (
            <div key={m.id} className="grid grid-cols-[1fr_auto_auto_auto] gap-3 px-4 py-3 items-center">
              <div className="min-w-0">
                <p className="text-white text-sm font-medium truncate">{m.label}</p>
                <p className="text-white/30 text-[10px]">Bonus: ₦{Number(vals[m.amountKey] ?? 0).toLocaleString()}{savingKey?.startsWith(`reward.${m.id}`) ? " · saving…" : ""}</p>
              </div>
              <div className="flex justify-center"><Toggle k={`reward.${m.id}.enabled`} /></div>
              <div className="flex justify-center">
                {m.noKyc ? <span className="text-white/20 text-xs">n/a</span> : <Toggle k={`reward.${m.id}.require_kyc`} fallback={vals["reward.require_kyc_default"] ?? true} />}
              </div>
              <div className="flex justify-end">
                {m.hasMin ? <NumInput k={`reward.${m.id}.min_amount`} /> : <span className="text-white/20 text-xs">—</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <p className="text-white/30 text-xs mt-4 leading-relaxed">
        <strong className="text-white/50">How the gates work:</strong> a reward pays only if the master switch is on,
        the reward is on, the user is KYC-approved (when KYC is required), and the transaction meets the minimum.
        Setting a minimum above ₦0 means the amount must be passed by the backend — deposits, buys and sells already do.
        These changes apply immediately (cache is invalidated on save).
      </p>
    </div>
  );
}
